import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../../Config/axiosInstance";
import AddInvoiceProcurement from "./AddInvoiceProcurement";
import EditInvoiceProcurement from "./EditInvoiceProcurement";
import PaymentInvoiceProcurement from "./PaymentInvoiceProcurement";
import "./InvoiceProcurement.css";
import { Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

function InvoiceProcurement() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canViewPayment, setCanViewPayment] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const formatNumber = (value, locale = "ro-RO") => {
    if (value === null || value === undefined || value === "") return "";

    const n = Number(value);
    if (Number.isNaN(n)) return "";

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  useEffect(() => {
    const requiredPath = "/invoice-procurement";
    const requiredPathPayment =
      "/invoice-procurement/payment-invoice-procurement";

    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPath && page.can_create
      )
    );

    setCanEdit(
      permissions.some(
        (page) => new URL(page.url).pathname === requiredPath && page.can_edit
      )
    );

    setCanViewPayment(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPathPayment && page.can_view
      )
    );

    setCanDelete(
      permissions.some(
        (page) =>
          new URL(page.url).pathname === requiredPath && page.can_delete
      )
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const invoiceResponse = await axiosInstance.get("/invoice-procurement/");

        const sortedData = invoiceResponse.data
          .map((item) => {
            // determină dacă este credit_note
            const isRevision = item.original_invoice !== null && item.original_invoice !== undefined;
            let contractNumber = "-";
            let purchaseOrderNumber = "-";
            let originalInvoiceNumber = "-";

            if (isRevision) {
              contractNumber = item.contract_display || "-";
              purchaseOrderNumber = item.purchase_order_display || "-";
              originalInvoiceNumber = item.original_invoice_display || "-";
              if (contractNumber === "-" && item.contract) {
                contractNumber = `ID: ${item.contract}`;
              }

              if (purchaseOrderNumber === "-" && item.purchase_order) {
                purchaseOrderNumber = `ID: ${item.purchase_order}`;
              }
            } else {
              contractNumber = item.contract_display || "-";
              purchaseOrderNumber = item.purchase_order_display || "-";

              if (contractNumber === "-" && item.contract) {
                contractNumber = `ID: ${item.contract}`;
              }

              if (purchaseOrderNumber === "-" && item.purchase_order) {
                purchaseOrderNumber = `ID: ${item.purchase_order}`;
              }
            }

            return {
              id: item.id,
              invoice_number: item.invoice_number || 'N/A',
              project_code: item.project_code_display || "-",
              subproject_code: item.subproject_code_display || "-",
              contract: contractNumber,
              purchase_order: purchaseOrderNumber,
              original_invoice_number: originalInvoiceNumber,
              cost_id: item.cost_display || "Not Available",
              invoice_without_vat: item.invoice_without_vat ?? null,
              total_invoice_with_vat: item.total_invoice_with_vat ?? null,
              invoice_without_vat_system: item.invoice_without_vat_system ?? null,
              total_invoice_with_vat_system: item.total_invoice_with_vat_system ?? null,
              credit_note_without_vat: item.credit_note_without_vat ?? null,
              credit_note_with_vat: item.credit_note_with_vat ?? null,
              credit_note_without_vat_system: item.credit_note_without_vat_system ?? null,
              credit_note_with_vat_system: item.credit_note_with_vat_system ?? null,
              difference_without_vat: item.difference_without_vat ?? null,
              difference_with_vat: item.difference_with_vat ?? null,
              difference_without_vat_system: item.difference_without_vat_system ?? null,
              difference_with_vat_system: item.difference_with_vat_system ?? null,
              currency: item.currency,
              currency_label: item.currency_display || "-",
              exchange_rate: item.exchange_rate || "",
              exchange_rate_value: item.exchange_rate_value || null,
              system_currency_label: item.system_currency_display || "System",
              bill_maturity: item.bill_maturity,
              supplier: item.supplier_display || "N/A",
              payments_status: item.payments_status || "Unpaid",
              invoice_date: item.invoice_date,
              original_invoice: item.original_invoice || null,
            };
          })
          .sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error("Error fetching data: ", error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  const handleAddClick = () =>
    navigate("/invoice-procurement/add-invoice-procurement");

  const handleEditClick = (row) =>
    navigate(`/invoice-procurement/edit-invoice-procurement/${row.id}`);

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/invoice-procurement/${itemToDelete.id}/`);
      setRows((prevRows) => prevRows.filter((r) => r.id !== itemToDelete.id));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error(
        "Error deleting invoice:",
        error.response?.data || error.message
      );
    }
  };

  const statusColors = {
    "Partially paid": "#ff9800",
    Paid: "#4caf50",
    Unpaid: "#f44336",
  };

  const columns = [
    {
      field: "invoice_number",
      headerName: "Invoice Number",
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "invoice_date",
      headerName: "Invoice Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const date = params.value ? new Date(params.value) : null;
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {date ? date.toLocaleDateString("ro-RO") : "-"}
          </div>
        )
      }
    },
    {
      field: "project_code",
      headerName: "Project Code",
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => {
        const value = params.value;
        if (!value || value === "-") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>–</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {value}
          </div>
        );
      },
    },
    {
      field: "subproject_code",
      headerName: "Subproject Code",
      flex: 1.3,
      minWidth: 140,
      renderCell: (params) => {
        const value = params.value;
        if (!value || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>–</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {value}
          </div>
        );
      },
    },
    {
      field: "contract",
      headerName: "Contract no.",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "purchase_order",
      headerName: "Purchase number",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "original_invoice_number",
      headerName: "Original Invoice",
      flex: 1.1,
      minWidth: 140,
      renderCell: (params) => {
        const value = params.value;
        if (!value || value === "-" || value === "ID: null") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            <span style={{ color: "#1565c0", fontWeight: "500" }}>{value}</span>
          </div>
        );
      },
    },
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 1.3,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "invoice_without_vat",
      headerName: "Invoice without VAT",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>–</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "total_invoice_with_vat",
      headerName: "Total invoice with VAT",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const value = params.value;
        if (value == null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "credit_note_without_vat",
      headerName: "Credit Note Without VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: 'center', height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "credit_note_with_vat",
      headerName: "Credit Note With VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "difference_without_vat",
      headerName: "Difference Without VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "difference_with_vat",
      headerName: "Difference with VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "invoice_without_vat_system",
      headerName: "Without VAT System",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "total_invoice_with_vat_system",
      headerName: "With VAT System",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "credit_note_without_vat_system",
      headerName: "Credit Note System Without VAT",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "credit_note_with_vat_system",
      headerName: "Credit Note System With VAT",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        );
      },
    },
    {
      field: "difference_without_vat_system",
      headerName: "System Difference Without VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "difference_with_vat_system",
      headerName: "System Difference With VAT",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return <div style={{ display: "flex", alignItems: "center", height: "100%", color: "#999" }}>-</div>
        }
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {formatNumber(value)} {params.row.system_currency_label || ""}
          </div>
        )
      }
    },
    {
      field: "bill_maturity",
      headerName: "Bill Maturity",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const date = params.value ? new Date(params.value) : null;
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            {date ? date.toLocaleDateString("ro-RO") : "–"}
          </div>
        );
      },
    },
    {
      field: "payments_status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <span
            style={{
              backgroundColor: statusColors[params.value] || "#ccc",
              color: "white",
              padding: "5px 10px",
              borderRadius: "10px",
              width: "90px",
              maxWidth: "90px",
              minWidth: "90px",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "15px",
            }}
          >
            {params.value}
          </span>
        </div>
      ),
    },
  ];

  if (canEdit || canViewPayment || canDelete) {
    columns.push({
      field: "action",
      headerName: "Action",
      width: 150,
      minWidth: 150,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          {canEdit && (
            <IconButton
              onClick={() => handleEditClick(params.row)}
              sx={{ color: "#1976d2" }}
            >
              <EditNoteSharpIcon />
            </IconButton>
          )}

          {canViewPayment && (
            <IconButton
              onClick={() =>
                navigate(
                  `/invoice-procurement/payment-invoice-procurement/${params.row.id}`
                )
              }
            >
              <VisibilityIcon sx={{ color: "#1976d2" }} />
            </IconButton>
          )}

          {canDelete && (
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              sx={{ color: "#d32f2f" }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="invoice-procurement-page">
            <div className="orders-additional-pappers-container">
              <h2 className="title">PROCUREMENT INVOICES</h2>

              {canCreate && (
                <div className="add-button-container">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ float: "right", marginBottom: "40px" }}
                  >
                    Add Invoice
                  </Button>
                </div>
              )}
            </div>

            <Dialog
              open={openDeleteDialog}
              onClose={() => setOpenDeleteDialog(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>

              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to delete invoice{" "}
                  {itemToDelete?.invoice_number}?
                </DialogContentText>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => setOpenDeleteDialog(false)}
                  sx={{
                    backgroundColor: "#e0e0e0",
                    color: "#424242",
                    "&:hover": {
                      backgroundColor: "#bdbdbd",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleConfirmDelete}
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                  }}
                  autoFocus
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            <div className="table-container">
              <DataGrid
                id="supplier-contract-table"
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                pageSize={5}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "id", sort: "desc" }],
                  },
                  pagination: {
                    paginationModel: { pageSize: 12 },
                  },
                }}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                pageSizeOptions={[10, 12, 20, 25]}
              />
            </div>
          </div>
        }
      />

      <Route
        path="add-invoice-procurement"
        element={<AddInvoiceProcurement />}
      />

      <Route
        path="edit-invoice-procurement/:id"
        element={<EditInvoiceProcurement />}
      />

      <Route
        path="payment-invoice-procurement/:id"
        element={<PaymentInvoiceProcurement />}
      />
    </Routes>
  );
}

export default InvoiceProcurement;